import { useCallback, useEffect, useMemo } from "react";
import { useAppSelector, useAppDispatch } from "../../../../../redux/store";
import {
  Drawer,
  Box,
  Typography,
  Grid,
  IconButton,
  Alert,
  Button,
  TextField,
  InputLabel,
  Card,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import { PatternFormat } from "react-number-format";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// import { useSnackbar } from "notistack";

import {
  closeProjectDrawer,
  changeProjectData,
  updateProjectPaymentMethod,
  deleteProjectPaymentMethod,
  resetProjectsMessages,
  setEditingBillingAddress,
} from "../../../../../redux/slices/projectsSlice";
import BillingAddressEditForm from "./BillingAddressEditForm";

const ProjectDetailsDrawer = ({ maxWidth }: any) => {
  const dispatch = useAppDispatch();
  const {
    fetchProjectsStatus,
    selectedProject,
    isDrawerOpen,
    updateProjectPaymentMethodError,
    changes: projectChanges,
    updateProjectPaymentMethodStatus,
    deleteProjectPaymentMethodStatus,
    editingBillingAddress,
  } = useAppSelector((state) => state.projects) as any;

  // Intuit does not ask for a billing address to take an ACH debit; only
  // Bill.com does. Showing, let alone asking for, one on that rail is a field
  // the payment never uses — it is kept in the books instead.
  //
  // Which rail is read from the project's own source, not from the portal's
  // switch: the switch can be showing the other side while this client is paid
  // on Intuit, which is the case the switch alone got wrong.
  const activeSource = useAppSelector((state) => state.source.source);
  const isIES = (selectedProject?.source || activeSource) === "ies";

  useEffect(() => {
    // Reset the project message when the drawer is closed
    if (!selectedProject) {
      dispatch(resetProjectsMessages());
    }
  }, [dispatch, selectedProject]);

  const handleUpdatePaymentMethod = () => {
    // Initialize data as an empty object
    let data: any = {};

    if (projectChanges) {
      const accountNumber = projectChanges?.accountNumber;

      if (accountNumber) {
        data.accountNumber = accountNumber;
      }

      const routingNumber = projectChanges?.routingNumber;

      if (routingNumber) {
        data.routingNumber = routingNumber;
      }

      const accountName = projectChanges?.accountName;
      if (accountName) {
        data.accountName = accountName;
      }

      // Intuit requires a phone number on the account it stores; without one on
      // the request it falls back to the customer record, which a client may
      // not have filled in.
      const phone = projectChanges?.phone;
      if (phone) {
        data.phone = phone;
      }

      const isSavings = projectChanges?.isSavings || false;
      const isPersonalAcct = projectChanges?.isPersonalAcct || false;
      data.isSavings = isSavings;
      data.isPersonalAcct = isPersonalAcct;

      // Dispatch only if data is not empty
      if (Object.keys(data).length > 0) {
        dispatch(
          updateProjectPaymentMethod({
            projectId: selectedProject.providerId,
            ...data,
          })
        );
      }
    }
  };

  const handleClose = () => {
    dispatch(closeProjectDrawer());
  };

  const onChange = (e: any) => {
    const { name, value } = e.target;
    dispatch(
      changeProjectData({
        name,
        value,
      })
    );
  };

  const handleAccountTypeChange = (_event: any, newAccountType: any) => {
    if (newAccountType !== null) {
      dispatch(
        changeProjectData({
          name: "isSavings",
          value: newAccountType,
        })
      );
    }
  };

  const handleAccountVariantChange = (_event: any, newAccountVariant: any) => {
    if (newAccountVariant !== null) {
      dispatch(
        changeProjectData({
          name: "isPersonalAcct",
          value: newAccountVariant,
        })
      );
    }
  };

  const handleDeleteAccount =
    (accountId: any, customerId?: any) => () => {
      // An account held by QuickBooks Payments is addressed by both ids: the
      // account's own, and the customer it hangs off.
      dispatch(deleteProjectPaymentMethod({ accountId, customerId }));
    };

  const loading = useMemo(
    () =>
      updateProjectPaymentMethodStatus === "loading" ||
      deleteProjectPaymentMethodStatus === "loading" ||
      fetchProjectsStatus === "loading",
    [
      updateProjectPaymentMethodStatus,
      deleteProjectPaymentMethodStatus,
      fetchProjectsStatus,
    ]
  );

  const handleEditBillingAddress = useCallback(() => {
    dispatch(setEditingBillingAddress(true));
  }, [dispatch]);

  // Helper function to calculate account type based on flags
  const getAccountTypeDisplay = (account: any) => {
    if (!account) return "N/A";
    
    const isSavings = account.isSavings || false;
    const isPersonal = account.isPersonalAcct || false;
    
    if (isSavings && isPersonal) return "Personal Savings";
    if (isSavings && !isPersonal) return "Business Savings";
    if (!isSavings && isPersonal) return "Personal Checking";
    if (!isSavings && !isPersonal) return "Business Checking";
    
    return "N/A";
  };

  return (
    <Drawer
      open={!!isDrawerOpen}
      onClose={handleClose}
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "rgba(19, 21, 23, 0.45)",
        },
        "& .MuiDrawer-paper": {
          mt: 7,
          height: "calc(100% - 56px)",
          borderRadius: 0,
          backgroundColor: "var(--surface-container-lowest, #ffffff)",
          border: "1px solid var(--outline-variant, #dcdcdc)",
          width: "100%",
          maxWidth: `${maxWidth ? maxWidth : 600}px`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          transition: "transform 225ms cubic-bezier(0, 0, 0.2, 1)",
        },
      }}
      anchor="right"
    >
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: 2,
          borderBottom: "1px solid var(--outline-variant, #dcdcdc)",
        }}
      >
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {selectedProject?.name}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: { xs: 2.5, sm: 3 },
          flex: 1,
          overflowY: "auto",
          "& .MuiOutlinedInput-input": {
            p: "4.5px 14px",
            backgroundColor: "#FFFFFF",
          },
          "& .MuiOutlinedInput-root": {
            borderRadius: 0,
          },
          "& .MuiTextField-root": {
            mt: 0,
          },
          "& .MuiButton-root": {
            borderRadius: 0,
            p: "2.75px 15px",
          },
        }}
      >
        <Grid container rowSpacing={2.5}>
          <Grid size={24}>
            {!isIES && (
              <>
                <Divider
                  textAlign="left"
                  sx={{ mb: 1.5, fontSize: 12, width: "100%" }}
                >
                  Billing Address
                </Divider>
            {editingBillingAddress ? <BillingAddressEditForm /> : null}
            {selectedProject?.billingAddress ? (
              <Card
                sx={{
                  borderRadius: 0,
                  backgroundColor: "#f7f7f7",
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 0.5 }}
                    >
                      {selectedProject.billingAddress?.billAddress}
                    </Typography>
                    {selectedProject.billingAddress?.billAddress2 && (
                      <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 0.5 }}
                      >
                        {selectedProject.billingAddress?.billAddress}
                      </Typography>
                    )}
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      sx={{ mb: 0.5 }}
                    >
                      {selectedProject.billingAddress?.billAddressCity}
                      {selectedProject.billingAddress?.billAddressState &&
                        `, ${selectedProject.billingAddress?.billAddressState}`}
                      {selectedProject.billingAddress?.billAddressZip &&
                        ` ${selectedProject.billingAddress?.billAddressZip}`}
                    </Typography>
                    {selectedProject.billingAddress?.billAddressCountry && (
                      <Typography variant="body2" color="textSecondary">
                        {selectedProject.billingAddress?.billAddressCountry}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    sx={() => ({
                      color: "text.secondary",
                      // "&:hover": { color: theme.palette.error.main },
                    })}
                    size="small"
                    onClick={handleEditBillingAddress}
                    disabled={loading}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Card>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>
                <Box>
                  <Typography variant="body2">
                    No billing address provided. Please update the project
                    details to add a billing address.
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    fullWidth
                    onClick={handleEditBillingAddress}
                    sx={{
                      mt: 1,
                      borderRadius: 0,
                      p: "2.75px 15px",
                    }}
                  >
                    Edit Billing Address
                  </Button>
                </Box>
              </Alert>
            )}
              </>
            )}
          </Grid>
          {selectedProject?.bankAccounts ? (
            <Divider
              textAlign="left"
              sx={{ mt: 2, mb: 1, fontSize: 12, width: "100%" }}
            >
              Bank Accounts
            </Divider>
          ) : null}
          {selectedProject?.bankAccounts?.map((account: any) => (
            <Grid size={{ xs: 12 }} key={account?.accountID}>
              <Card
                sx={{
                  borderRadius: 0,
                  backgroundColor: "#f7f7f7",
                  "&:hover": {
                  },
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                      {account?.accountName || "Bank Account"}
                    </Typography>
                    <IconButton
                      sx={(theme) => ({
                        color: "text.secondary",
                        "&:hover": { color: theme.palette.error.main },
                      })}
                      size="small"
                      onClick={handleDeleteAccount(
                        account?.accountID,
                        account?.customerID || selectedProject?.providerId,
                      )}
                      disabled={loading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    Account Type:{" "}
                    <span style={{ fontFamily: "Brandon_Grotesque_medium" }}>
                      {getAccountTypeDisplay(account)}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Routing Number:{" "}
                    <span style={{ fontFamily: "Brandon_Grotesque_medium" }}>
                      {account?.routingNumber || "N/A"}
                    </span>
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Account Number:{" "}
                    <span style={{ fontFamily: "Brandon_Grotesque_medium" }}>
                      {account?.accountNumber || "N/A"}
                    </span>
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
          <Divider
            textAlign="left"
            sx={{ mt: 1, mb: 1.5, fontSize: 12, width: "100%" }}
          >
            Add Bank Account
          </Divider>
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Name on Account
            </InputLabel>
            <TextField
              id="accountName"
              name="accountName"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              disabled={loading}
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter the name on the account"
              value={projectChanges?.accountName || ""}
              onChange={onChange}
              autoComplete="off"
            />
          </Grid>{" "}
          <Grid size={12}>
            <InputLabel htmlFor="accountType" sx={{ fontSize: 14 }}>
              Account Type
            </InputLabel>
            <ToggleButtonGroup
              value={projectChanges?.isSavings || false}
              exclusive
              onChange={handleAccountTypeChange}
              aria-label="account type"
              sx={{
                // mt: 1,
                width: "100%",
                "& .MuiToggleButton-root": {
                  borderRadius: 0,
                  padding: "2.75px 15px",
                  fontSize: 14,
                  flex: 1,
                  "&.Mui-selected": {
                    // backgroundColor: "#555759",
                    // color: "#ffffff",
                    // "&:hover": {
                    //   backgroundColor: "#555759",
                    // },
                  },
                  "&:not(:first-of-type)": {
                    marginLeft: "8px",
                    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                },
              }}
            >
              <ToggleButton
                value={false}
                disabled={loading}
                aria-label="checking account"
              >
                Checking
              </ToggleButton>
              <ToggleButton
                value={true}
                disabled={loading}
                aria-label="savings account"
              >
                Savings
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid size={12}>
            <ToggleButtonGroup
              value={projectChanges?.isPersonalAcct || false}
              exclusive
              onChange={handleAccountVariantChange}
              aria-label="account type"
              sx={{
                // mt: 1,
                width: "100%",
                "& .MuiToggleButton-root": {
                  borderRadius: 0,
                  padding: "2.75px 15px",
                  fontSize: 14,
                  flex: 1,
                  "&.Mui-selected": {
                    // backgroundColor: "#555759",
                    // color: "#ffffff",
                    // "&:hover": {
                    //   backgroundColor: "#555759",
                    // },
                  },
                  "&:not(:first-of-type)": {
                    marginLeft: "8px",
                    borderLeft: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                },
              }}
            >
              <ToggleButton
                value={false}
                disabled={loading}
                aria-label="savings account"
              >
                Business
              </ToggleButton>
              <ToggleButton
                value={true}
                disabled={loading}
                aria-label="checking account"
              >
                Personal
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Routing Number
            </InputLabel>
            <TextField
              id="routingNumber"
              name="routingNumber"
              variant="outlined"
              size="small"
              fullWidth
              // type="number"
              margin="normal"
              disabled={loading}
              error={!!updateProjectPaymentMethodError}
              placeholder="Enter your routing number"
              value={projectChanges?.routingNumber || ""}
              onChange={onChange}
            />
          </Grid>
          <Grid size={12}>
            {/* Intuit requires a phone number on the bank account it stores, and
                falls back to the customer record — which a client may not have.
                Asking here is what lets them add an account at all. */}
            <InputLabel htmlFor="phone" sx={{ fontSize: 14 }}>
              Phone Number
            </InputLabel>
            <PatternFormat
              customInput={TextField}
              format="(###) ###-####"
              id="phone"
              name="phone"
              variant="outlined"
              size="small"
              fullWidth
              margin="normal"
              autoComplete="tel"
              inputMode="tel"
              disabled={loading}
              error={!!updateProjectPaymentMethodError}
              placeholder="(555) 123-4567"
              value={projectChanges?.phone || ""}
              onValueChange={(values) =>
                dispatch(changeProjectData({ name: "phone", value: values.value }))
              }
            />
          </Grid>
          <Grid size={12}>
            <InputLabel htmlFor="email" sx={{ fontSize: 14 }}>
              Account Number
            </InputLabel>
            <TextField
              id="accountNumber"
              name="accountNumber"
              variant="outlined"
              size="small"
              fullWidth
              // type="number"
              margin="normal"
              disabled={loading}
              error={!!updateProjectPaymentMethodError}
              placeholder={
                selectedProject?.bankAccount?.accountNumber ||
                "Enter full account number"
              }
              value={projectChanges?.accountNumber || ""}
              onChange={onChange}
            />
          </Grid>
          {selectedProject?.bankAccounts === null ||
          selectedProject?.bankAccounts?.length === 0 ? (
            <Grid size={12}>
              <Alert severity="warning">
                No bank account linked. Please add a bank account to be able to
                make payments.
              </Alert>
            </Grid>
          ) : null}
          <Grid size={12}>
            <Button
              sx={{
                borderRadius: 0,
              }}
              fullWidth
              color="success"
              variant="contained"
              onClick={handleUpdatePaymentMethod}
              disabled={loading || !projectChanges}
            >
              Add Payment Method
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Drawer>
  );
};

export default ProjectDetailsDrawer;
